import { createSelector } from "reselect";
import { AppState } from "..";
import { userGetInventoryTypeSelector } from "../system/selector";
import {
  GeneralLedgerAccountTypeOrder,
  generalLedgerAccountTypeToLabel,
} from "src/utils/general-ledger-accounts";
import type {
  GeneralLedgerAccountV2,
  GeneralLedgerAccountV2Type,
} from "src/interfaces/generalLedgerAccount.interface";

const getGeneralLedgerAccountState = (state: AppState) => state.glAccounts;

export const getGeneralLedgerAccounts = createSelector(
  getGeneralLedgerAccountState,
  (state) => state.items
);

export const generalLedgerAccountsSelector = createSelector(
  [getGeneralLedgerAccounts, userGetInventoryTypeSelector],
  (accounts, inventoryType) => {
    return accounts.reduce((accounts, account) => {
      if (
        !(
          "inventoryType" in account &&
          inventoryType === "cash" &&
          inventoryType !== account.inventoryType
        )
      ) {
        accounts.push(account);
      }

      return accounts;
    }, [] as typeof accounts);
  }
);

export const generalLedgerAccountsSortedTreeSelector = createSelector(
  [generalLedgerAccountsSelector],
  (accounts) => {
    const accountsByType = accounts.reduce((byType, account) => {
      if (!byType[account.type[0]]) byType[account.type[0]] = [];
      byType[account.type[0]].push(account);
      return byType;
    }, {} as Record<GeneralLedgerAccountV2Type[0], GeneralLedgerAccountV2[]>);

    const tree: {
      type: GeneralLedgerAccountV2Type[0];
      name: string;
      accounts: GeneralLedgerAccountV2[];
    }[] = [];
    for (const type of GeneralLedgerAccountTypeOrder) {
      const accounts = accountsByType[type];
      const name = generalLedgerAccountTypeToLabel([type, ""] as any)[0];
      if (accounts) tree.push({ 
        type, 
        name, 
        accounts: [...accounts].sort((a, b) => a.name.localeCompare(b.name))
       });
    }
    return tree;
  }
);

export const generalLedgerAccountSelector = createSelector(
  [
    generalLedgerAccountsSelector,
    (_, { id }: { id?: string }) => id,
    (_, { name }: { name?: string }) => name,
  ],
  (accounts, id, name) => {
    if (id) {
      const account = accounts.find((a) => a.id === id);
      if (account) return account;
    }

    if (name) {
      const account = accounts.find((a) => a.name === name);
      if (account) return account;
    }

    return undefined;
  }
);

export const userGeneralLedgerAccountsSelector = createSelector(
  [generalLedgerAccountsSelector],
  (accounts) => accounts.filter((a) => !a.default)
);
